import type { IDAChatMessage } from "./chatMessageMapper";
import type { GptLiveTranscriptBoundary } from "../services/gptLiveVoiceTransport";
import { preserveUserReplyOrder } from "./userReplyOrder";

export const GPT_LIVE_TRANSCRIPT_COMMIT_RETRY_MS = 350;

/** Refresh persisted rows at the same boundary that flushes a live voice run. */
export function refreshVoiceTranscriptAtBoundary(
  boundary: GptLiveTranscriptBoundary,
  refresh: () => void,
  schedule: (callback: () => void, delayMs: number) => unknown = (
    callback,
    delayMs,
  ) => window.setTimeout(callback, delayMs),
): void {
  if (boundary === null) return;
  refresh();
  schedule(refresh, GPT_LIVE_TRANSCRIPT_COMMIT_RETRY_MS);
}

const GPT_LIVE_BOUNDARY_SKEW_TOLERANCE_MS = 250;

interface VoiceTranscriptRange {
  providerSessionId: string;
  speaker: string;
  startMs: number;
  endMs: number;
}

interface VoiceTranscriptEventIdentity {
  providerSessionId: string;
  speaker: string;
  eventIds: string[];
}

/** Show the cursor only on the newest unfinished assistant voice run while its audio is audible. */
export function applyVoiceAssistantPlaybackState(
  messages: IDAChatMessage[],
  audioIsSpeaking: boolean,
): IDAChatMessage[] {
  let activeAssistantIndex = -1;
  if (audioIsSpeaking) {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (
        message.role === "assistant" &&
        message.metadata?.gpt_live_run_complete !== true
      ) {
        activeAssistantIndex = index;
        break;
      }
    }
  }

  let changed = false;
  const result = messages.map((message, index) => {
    if (typeof message.metadata?.gpt_live_batch_id !== "string") return message;
    const isStreaming = index === activeAssistantIndex;
    if (Boolean(message.isStreaming) === isStreaming) return message;
    changed = true;
    return { ...message, isStreaming };
  });
  return changed ? result : messages;
}

function getVoiceTranscriptEventIdentity(
  message: IDAChatMessage,
): VoiceTranscriptEventIdentity | null {
  const metadata = message.metadata;
  if (
    typeof metadata?.provider_session_id !== "string" ||
    typeof metadata.speaker !== "string" ||
    !Array.isArray(metadata.provider_event_ids)
  ) {
    return null;
  }
  const eventIds = metadata.provider_event_ids.filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );
  return eventIds.length
    ? {
        providerSessionId: metadata.provider_session_id,
        speaker: metadata.speaker,
        eventIds,
      }
    : null;
}

function persistedVoiceRowsMatchEvents(
  provisionalIdentity: VoiceTranscriptEventIdentity,
  persistedMessages: IDAChatMessage[],
): boolean {
  const eventIds = new Set(provisionalIdentity.eventIds);
  return persistedMessages.some((message) => {
    if (
      message.metadata?.source !== "voice" ||
      message.role !== provisionalIdentity.speaker
    ) {
      return false;
    }
    const identity = getVoiceTranscriptEventIdentity(message);
    return (
      identity !== null &&
      identity.providerSessionId === provisionalIdentity.providerSessionId &&
      identity.speaker === provisionalIdentity.speaker &&
      identity.eventIds.some((eventId) => eventIds.has(eventId))
    );
  });
}

function getVoiceTranscriptRange(
  message: IDAChatMessage,
): VoiceTranscriptRange | null {
  const metadata = message.metadata;
  if (
    typeof metadata?.provider_session_id !== "string" ||
    typeof metadata.speaker !== "string" ||
    typeof metadata.start_ms !== "number" ||
    typeof metadata.end_ms !== "number" ||
    !Number.isFinite(metadata.start_ms) ||
    !Number.isFinite(metadata.end_ms) ||
    metadata.end_ms < metadata.start_ms
  ) {
    return null;
  }
  return {
    providerSessionId: metadata.provider_session_id,
    speaker: metadata.speaker,
    startMs: metadata.start_ms,
    endMs: metadata.end_ms,
  };
}

function persistedVoiceRowsCoverRange(
  provisionalRange: VoiceTranscriptRange,
  persistedMessages: IDAChatMessage[],
): boolean {
  const rangeLength = provisionalRange.endMs - provisionalRange.startMs;
  const toleranceMs = Math.min(
    GPT_LIVE_BOUNDARY_SKEW_TOLERANCE_MS,
    rangeLength * 0.1,
  );
  const intervals = persistedMessages
    .filter(
      (message) =>
        message.metadata?.source === "voice" &&
        message.role === provisionalRange.speaker,
    )
    .map(getVoiceTranscriptRange)
    .filter(
      (range): range is VoiceTranscriptRange =>
        range !== null &&
        range.providerSessionId === provisionalRange.providerSessionId &&
        range.startMs <= provisionalRange.endMs &&
        range.endMs >= provisionalRange.startMs,
    )
    .sort((left, right) => left.startMs - right.startMs);

  let coveredThrough = provisionalRange.startMs;
  let startedCoverage = false;
  for (const interval of intervals) {
    if (
      !startedCoverage &&
      interval.startMs <= provisionalRange.startMs + toleranceMs
    ) {
      coveredThrough = interval.endMs;
      startedCoverage = true;
    } else if (
      startedCoverage &&
      interval.startMs <= coveredThrough + toleranceMs
    ) {
      coveredThrough = Math.max(coveredThrough, interval.endMs);
    }
    if (
      startedCoverage &&
      coveredThrough >= provisionalRange.endMs - toleranceMs
    ) {
      return true;
    }
  }
  return false;
}

/** Reconcile finalized bubbles by provider session, speaker and timeline coverage. */
export function reconcileProvisionalVoiceMessages(
  provisionalMessages: IDAChatMessage[],
  persistedMessages: IDAChatMessage[],
): IDAChatMessage[] {
  const remainingMessages = provisionalMessages.filter((provisional) => {
    if (provisional.isStreaming) return true;
    if (provisional.metadata?.gpt_live_run_complete === false) return true;
    const delegationStartedAt =
      provisional.role === "assistant" &&
      typeof provisional.metadata?.gpt_live_delegation_started_at === "number"
        ? provisional.metadata.gpt_live_delegation_started_at
        : null;
    if (
      delegationStartedAt !== null &&
      persistedMessages.some(
        (message) =>
          message.role === "assistant" &&
          message.metadata?.source === "voice" &&
          message.metadata.speaker === "assistant" &&
          message.metadata.provider_session_id ===
            provisional.metadata?.provider_session_id &&
          typeof message.metadata.voice_turn_id !== "string" &&
          typeof message.metadata.start_ms !== "number" &&
          typeof message.metadata.end_ms !== "number" &&
          message.hubMessageId !== undefined &&
          message.createdAt.getTime() > delegationStartedAt,
      )
    ) {
      return false;
    }
    const provisionalRange = getVoiceTranscriptRange(provisional);
    const provisionalEventIdentity =
      getVoiceTranscriptEventIdentity(provisional);
    const hasTimeline =
      typeof provisional.metadata?.start_ms === "number" &&
      typeof provisional.metadata?.end_ms === "number";
    const hasEventIds = provisionalEventIdentity !== null;
    const savedUnkeyedRunArrived =
      !hasTimeline &&
      !hasEventIds &&
      typeof provisional.metadata?.provider_session_id === "string" &&
      persistedMessages.some(
        (message) =>
          message.metadata?.source === "voice" &&
          message.role === provisional.role &&
          message.metadata.speaker === provisional.metadata?.speaker &&
          message.metadata.provider_session_id ===
            provisional.metadata?.provider_session_id &&
          message.createdAt.getTime() >= provisional.createdAt.getTime(),
      );
    return (
      (provisionalRange === null ||
        !persistedVoiceRowsCoverRange(provisionalRange, persistedMessages)) &&
      (provisionalEventIdentity === null ||
        !persistedVoiceRowsMatchEvents(
          provisionalEventIdentity,
          persistedMessages,
        )) &&
      !savedUnkeyedRunArrived
    );
  });
  return remainingMessages;
}

/** Merge provisional voice turns with canonical persisted conversation rows. */
export function mergeVoiceTranscriptMessages(
  normalMessages: IDAChatMessage[],
  provisionalVoiceMessages: IDAChatMessage[],
): IDAChatMessage[] {
  const isProviderVoiceRow = (message: IDAChatMessage): boolean =>
    message.metadata?.source === "voice" &&
    typeof message.metadata.provider_session_id === "string" &&
    (Number.isFinite(message.metadata.start_ms) ||
      typeof message.metadata.voice_turn_id === "string");

  const unreconciledVoiceMessages = reconcileProvisionalVoiceMessages(
    provisionalVoiceMessages,
    normalMessages,
  );
  if (
    unreconciledVoiceMessages.length === 0 &&
    !normalMessages.some(isProviderVoiceRow)
  ) {
    return normalMessages;
  }

  const chatMessages = [
    ...normalMessages.filter((message) => !isProviderVoiceRow(message)),
    ...unreconciledVoiceMessages.filter(
      (message) => !isProviderVoiceRow(message),
    ),
  ];

  // Keep the established order of regular chat rows, without applying the
  // reply-order adjustment to provider transcript rows.
  const orderedChat = preserveUserReplyOrder(
    chatMessages,
    [...chatMessages].sort(
      (left, right) =>
        left.createdAt.getTime() - right.createdAt.getTime() ||
        left.id.localeCompare(right.id),
    ),
  );
  const orderedVoice = [
    ...normalMessages.filter(isProviderVoiceRow),
    ...unreconciledVoiceMessages.filter(isProviderVoiceRow),
  ].sort(
    (left, right) =>
      left.createdAt.getTime() - right.createdAt.getTime() ||
      left.id.localeCompare(right.id),
  );

  const ordered: IDAChatMessage[] = [];
  let voiceIndex = 0;
  for (const chatMessage of orderedChat) {
    while (
      voiceIndex < orderedVoice.length &&
      (orderedVoice[voiceIndex].createdAt.getTime() <
        chatMessage.createdAt.getTime() ||
        (orderedVoice[voiceIndex].createdAt.getTime() ===
          chatMessage.createdAt.getTime() &&
          orderedVoice[voiceIndex].id.localeCompare(chatMessage.id) < 0))
    ) {
      ordered.push(orderedVoice[voiceIndex]);
      voiceIndex += 1;
    }
    ordered.push(chatMessage);
  }
  ordered.push(...orderedVoice.slice(voiceIndex));

  // Hub timestamps reflect persistence time, not speech time. Restore provider
  // order only inside contiguous same-session runs, preserving real boundaries.
  let runStart = 0;
  while (runStart < ordered.length) {
    const first = ordered[runStart];
    if (!isProviderVoiceRow(first)) {
      runStart += 1;
      continue;
    }
    const sessionId = first.metadata?.provider_session_id;
    let runEnd = runStart + 1;
    while (
      runEnd < ordered.length &&
      isProviderVoiceRow(ordered[runEnd]) &&
      ordered[runEnd].metadata?.provider_session_id === sessionId
    ) {
      runEnd += 1;
    }
    const run = ordered.slice(runStart, runEnd);
    const allHaveProviderTime = run.every((message) =>
      Number.isFinite(message.metadata?.start_ms),
    );
    run.sort((left, right) =>
      allHaveProviderTime
        ? Number(left.metadata?.start_ms) - Number(right.metadata?.start_ms) ||
          left.createdAt.getTime() - right.createdAt.getTime() ||
          left.id.localeCompare(right.id)
        : left.createdAt.getTime() - right.createdAt.getTime() ||
          left.id.localeCompare(right.id),
    );
    ordered.splice(runStart, run.length, ...run);
    runStart = runEnd;
  }

  return ordered;
}
